import type { IdentitySlot } from './modelsLedger';

/**
 * A profile's identity colour (spec 251): one of five brand colours, paired per
 * theme in `globals.css`, then grey for a sixth profile and beyond. It marks who a
 * profile is, on its dot and its letter tile beside its name, and never fills a
 * bar, a chip or a meter: these hues double as status colours elsewhere, and a bare
 * block of teal would read as "success". The runs whose profile is not known have
 * no dot. Written out in full so Tailwind sees every class.
 */
export const IDENTITY_DOT: Record<IdentitySlot, string | null> = {
  1: 'bg-identity-dot-1',
  2: 'bg-identity-dot-2',
  3: 'bg-identity-dot-3',
  4: 'bg-identity-dot-4',
  5: 'bg-identity-dot-5',
  other: 'bg-muted-foreground',
  unknown: null,
};

/**
 * How a quantity is drawn on the Models tab (spec 251): runs are one measure, so the
 * Runs by profile bar and the ledger's Runs column draw them alike. The profile with
 * the most runs in brand blue, the rest in grey steps, the runs whose profile is not
 * known faintest. Colour marks what leads, not who: the profile's own colour stays on
 * its dot. Written out in full so Tailwind sees every class.
 */
const QUANTITY_STEPS = [
  'bg-chart-1',
  'bg-muted-foreground/70',
  'bg-muted-foreground/50',
  'bg-muted-foreground/35',
] as const;
const QUANTITY_UNKNOWN = 'bg-muted-foreground/25';

/** `rank` counts known profiles from 0, most runs first; `null` is the unknown. */
export function quantityFill(rank: number | null): string {
  if (rank === null) return QUANTITY_UNKNOWN;
  return QUANTITY_STEPS[Math.min(rank, QUANTITY_STEPS.length - 1)];
}

/**
 * The same steps for an SVG mark (spec 257): the Per day bars stack in the share bars'
 * fills, so a slice reads as the same thing on the chart and below it.
 */
const QUANTITY_SVG_STEPS = [
  'fill-chart-1',
  'fill-muted-foreground/70',
  'fill-muted-foreground/50',
  'fill-muted-foreground/35',
] as const;
const QUANTITY_SVG_UNKNOWN = 'fill-muted-foreground/25';

export function quantitySvgFill(rank: number | null): string {
  if (rank === null) return QUANTITY_SVG_UNKNOWN;
  return QUANTITY_SVG_STEPS[Math.min(rank, QUANTITY_SVG_STEPS.length - 1)];
}

/** The ring an idle profile's dot is drawn as (spec 252): its colour, hollow. */
const IDENTITY_RING: Record<IdentitySlot, string> = {
  1: 'border-identity-dot-1',
  2: 'border-identity-dot-2',
  3: 'border-identity-dot-3',
  4: 'border-identity-dot-4',
  5: 'border-identity-dot-5',
  other: 'border-muted-foreground',
  unknown: 'border-muted-foreground',
};

/**
 * A profile's dot (spec 252): filled in its colour when it ran; a ring in its colour
 * when it is configured but did not run in the period; a grey ring for Not recorded,
 * which is a gap in the data rather than a profile.
 */
export function identityDot(identity: {
  slot: IdentitySlot;
  idle?: boolean;
}): string {
  if (identity.slot === 'unknown' || identity.idle)
    return `bg-card border-2 ${IDENTITY_RING[identity.slot]}`;
  return IDENTITY_DOT[identity.slot] ?? 'bg-muted-foreground';
}
